import { SIM_VERSION } from "../sim/constants";
import type { ControlState } from "../sim/controls";
import { hashWorld } from "../sim/hash";
import type { WorldState } from "../sim/state";
import type { DesyncReport } from "./desync-report";
import { encodeInputs } from "./input-codec";
import type { NetClient } from "./net-client";
import {
	type EndReason,
	type MatchSetup,
	type PlayerSlot,
	type RematchSetup,
	type ServerMessage,
	setupOf,
} from "./protocol";
import { DEFAULT_ROLLBACK, type RollbackHooks, RollbackSession } from "./rollback";

/** What the online HUD shows. */
export type OnlineStatus =
	| { kind: "playing" }
	/** At the prediction limit: no inputs from the opponent for a while. */
	| { kind: "waiting" }
	/** The opponent dropped; the server keeps their seat until `until` (ms timestamp). */
	| { kind: "peer-left"; until: number }
	/** The connection to the server is gone. */
	| { kind: "disconnected" }
	| { kind: "ended"; reason: EndReason };

const PING_INTERVAL_MS = 2000;

/**
 * One online match from this browser's side: owns the relay connection's match traffic and
 * the rollback session. MatchScene calls `attach()` with its world, `advance()` per fixed step
 * and `flush()` once per frame.
 */
export class OnlineMatch {
	private session: RollbackSession | null = null;
	private status: OnlineStatus = { kind: "playing" };
	private readonly unsubscribe: Array<() => void> = [];
	private pingTimer: ReturnType<typeof setInterval> | null = null;
	private pingId = 0;
	private readonly pingSent = new Map<number, number>();
	/** Round trip to the relay server in ms (the opponent's latency adds to it). */
	rtt: number | null = null;
	/** First tick whose hashes disagreed (a bug: the matches have diverged). */
	desyncTick: number | null = null;
	onStatus: (status: OnlineStatus) => void = () => {};
	/**
	 * Someone asked for a rematch (P10.3). `setup`: the host picked a new map, rules or mode, and
	 * this player's earlier request no longer counts (I-013).
	 */
	onRematchWanted: (setup?: RematchSetup) => void = () => {};
	/** Both asked: the server started the next match in this room, on this connection. */
	onRematch: (next: OnlineMatch) => void = () => {};
	/** This player already asked for a rematch. */
	rematchRequested = false;
	/** Opponent inputs that arrived before the scene attached the session. */
	private readonly early: Array<{
		player: PlayerSlot;
		start: number;
		bits: number[];
		adv?: number;
	}> = [];

	/**
	 * @param token rejoins this seat after a dropped connection.
	 * @param resumeLogs both players' input logs when this is a rejoin (from `resume`).
	 */
	constructor(
		private readonly client: NetClient,
		readonly setup: MatchSetup,
		readonly slot: PlayerSlot,
		readonly token: string,
		private readonly resumeLogs: number[][] | null = null,
	) {
		this.unsubscribe.push(
			client.onMessage((m) => this.handle(m)),
			client.onClose(() => this.setStatus({ kind: "disconnected" })),
		);
		this.pingTimer = setInterval(() => this.ping(), PING_INTERVAL_MS);
	}

	get current(): OnlineStatus {
		return this.status;
	}

	get stats() {
		return this.session?.stats ?? null;
	}

	/** A report of the first desync for `pnpm desync` (P10.7), or null if there was none. */
	desyncReport(app: string): DesyncReport | null {
		if (this.desyncTick === null || !this.session) return null;
		const logs = this.inputLogs();
		const mine = this.session.stateAt(this.desyncTick);
		const own = mine ? hashWorld(mine) : null;
		return {
			format: "gravity-desync",
			sim: SIM_VERSION,
			app,
			date: new Date().toISOString(),
			setup: this.setup,
			inputs: logs.map(encodeInputs),
			tick: this.desyncTick,
			hashes: logs.map((_, i) => (i === this.slot ? own : null)),
			writer: this.slot,
			state: mine,
		};
	}

	/** Both players' inputs so far (the local one runs ahead by the input delay). */
	inputLogs(): Array<readonly number[]> {
		const players = this.setup.players ?? 2;
		return Array.from({ length: players }, (_, i) => this.session?.inputLog(i as PlayerSlot) ?? []);
	}

	get confirmedTick(): number {
		return this.session?.confirmedTick ?? 0;
	}

	/** Starts the rollback session on the scene's freshly created world. */
	attach(world: WorldState, hooks: RollbackHooks): void {
		const options = { ...DEFAULT_ROLLBACK, inputDelay: this.setup.inputDelay };
		this.session = this.resumeLogs
			? RollbackSession.resume(world, this.slot, this.resumeLogs, options, hooks)
			: new RollbackSession(world, this.slot, options, hooks);
		for (const m of this.early.splice(0)) this.receive(m.player, m.start, m.bits, m.adv);
	}

	/** One fixed step; false while waiting for the opponent (or after the match ended). */
	advance(local: Readonly<ControlState>): boolean {
		if (!this.session || this.status.kind === "ended" || this.status.kind === "disconnected") {
			return false;
		}
		const stepped = this.session.advance(local);
		if (this.status.kind === "playing" && this.session.stalled) this.setStatus({ kind: "waiting" });
		else if (this.status.kind === "waiting" && !this.session.stalled)
			this.setStatus({ kind: "playing" });
		return stepped;
	}

	/** Sends this frame's inputs and confirmed hashes. */
	flush(): void {
		if (!this.session) return;
		for (;;) {
			const out = this.session.takeOutgoing();
			if (!out) break;
			this.client.send({ t: "input", ...out });
		}
		for (const h of this.session.takeHashes()) this.client.send({ t: "hash", ...h });
	}

	/** Leaves the match (the opponent is told) and closes the connection. */
	leave(): void {
		this.client.send({ t: "leave" });
		this.dispose();
	}

	/** This player created the room: only they may change the rematch's setup (I-013). */
	get isHost(): boolean {
		return this.slot === 0;
	}

	/**
	 * Asks for another match in the same room (the opponent must accept). The host may pass a new
	 * map, rules and mode, also after already asking.
	 */
	requestRematch(setup?: RematchSetup): void {
		if (
			(this.rematchRequested && !setup) ||
			this.status.kind === "ended" ||
			this.status.kind === "disconnected"
		) {
			return;
		}
		this.rematchRequested = true;
		// A fresh round trip lets the server re-pick the input delay for the next match.
		this.client.send({
			t: "rematch",
			...(this.rtt === null ? {} : { rtt: Math.round(this.rtt) }),
			...(setup ? { setup } : {}),
		});
	}

	dispose(): void {
		this.detach();
		this.client.close();
	}

	/** Stops listening without closing the connection (handed to the next match). */
	private detach(): void {
		if (this.pingTimer !== null) clearInterval(this.pingTimer);
		this.pingTimer = null;
		for (const off of this.unsubscribe.splice(0)) off();
	}

	private handle(m: ServerMessage): void {
		switch (m.t) {
			case "input":
				if (m.player === this.slot) break;
				if (this.session) this.receive(m.player, m.start, m.bits, m.adv);
				else this.early.push(m);
				break;
			case "peer-left":
				this.setStatus({ kind: "peer-left", until: Date.now() + m.graceMs });
				break;
			case "peer-back":
				this.setStatus({ kind: "playing" });
				break;
			case "desync":
				this.desyncTick ??= m.tick;
				console.error(`gravity: online desync at tick ${m.tick}`);
				break;
			case "ended":
				this.setStatus({ kind: "ended", reason: m.reason });
				break;
			case "rematch-wanted":
				if (m.setup) this.rematchRequested = false;
				this.onRematchWanted(m.setup);
				break;
			case "start": {
				// A rematch: the next match takes over this connection.
				this.detach();
				this.onRematch(new OnlineMatch(this.client, setupOf(m), m.player, m.token));
				break;
			}
			case "pong": {
				const sent = this.pingSent.get(m.id);
				this.pingSent.delete(m.id);
				if (sent !== undefined) this.rtt = performance.now() - sent;
				break;
			}
			default:
				break;
		}
	}

	private receive(player: PlayerSlot, start: number, bits: number[], adv?: number): void {
		// Overlap with the resume logs is fine; the session ignores ticks it already has.
		this.session?.receiveRemote(start, bits, adv, player);
	}

	private ping(): void {
		const id = ++this.pingId;
		this.pingSent.set(id, performance.now());
		if (this.pingSent.size > 10) this.pingSent.delete(id - 10);
		this.client.send({ t: "ping", id });
	}

	private setStatus(status: OnlineStatus): void {
		if (this.status.kind === "ended") return; // final
		this.status = status;
		this.onStatus(status);
	}
}
